import { useState, useEffect, useCallback } from 'react'
import Modal from '../shared/Modal'
import { ChatRoom, ChatRoomMemberWithProfile } from '../../types/chat'
import { ChatService } from '../../services/chat.service'
import { useProfile } from '../../hooks/useProfile'

interface ChannelManagementProps {
  isOpen: boolean
  onClose: () => void
}

interface User {
  id: string
  display_name: string | null
  account_type: string | null
}

export default function ChannelManagement({ isOpen, onClose }: ChannelManagementProps) {
  const { profile } = useProfile()
  const isAdmin = profile?.account_type === 'admin' || profile?.account_type === 'global_admin'

  const [rooms, setRooms] = useState<ChatRoom[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create room state
  const [showCreateRoom, setShowCreateRoom] = useState(false)
  const [newRoomName, setNewRoomName] = useState('')
  const [newRoomDescription, setNewRoomDescription] = useState('')
  const [creating, setCreating] = useState(false)

  // Selected room for member management
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null)
  const [roomMembers, setRoomMembers] = useState<ChatRoomMemberWithProfile[]>([])
  const [loadingMembers, setLoadingMembers] = useState(false)

  const fetchRooms = useCallback(async () => {
    try {
      setLoading(true)
      const [fetchedRooms, fetchedUsers] = await Promise.all([
        ChatService.getAllRooms(),
        ChatService.getAllUsers(),
      ])
      setRooms(fetchedRooms)
      setUsers(fetchedUsers)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen && isAdmin) {
      fetchRooms()
    }
  }, [isOpen, isAdmin, fetchRooms])

  const fetchRoomMembers = useCallback(async (roomId: string) => {
    try {
      setLoadingMembers(true)
      const members = await ChatService.getRoomMembers(roomId)
      setRoomMembers(members)
    } catch (err) {
      console.error('Failed to load room members:', err)
    } finally {
      setLoadingMembers(false)
    }
  }, [])

  useEffect(() => {
    if (selectedRoom) {
      fetchRoomMembers(selectedRoom.id)
    }
  }, [selectedRoom, fetchRoomMembers])

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) return

    try {
      setCreating(true)
      await ChatService.createRoom({
        name: newRoomName,
        description: newRoomDescription || undefined,
      })
      setNewRoomName('')
      setNewRoomDescription('')
      setShowCreateRoom(false)
      await fetchRooms()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create room')
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteRoom = async (room: ChatRoom) => {
    if (room.is_default) return
    if (!window.confirm(`Delete channel "${room.name}"? This will delete all messages.`)) return

    try {
      await ChatService.deleteRoom(room.id)
      if (selectedRoom?.id === room.id) {
        setSelectedRoom(null)
        setRoomMembers([])
      }
      await fetchRooms()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete room')
    }
  }

  const handleAddMember = async (userId: string) => {
    if (!selectedRoom) return

    try {
      await ChatService.addMemberToRoom(selectedRoom.id, userId)
      await fetchRoomMembers(selectedRoom.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add member')
    }
  }

  const handleRemoveMember = async (userId: string) => {
    if (!selectedRoom) return

    try {
      await ChatService.removeMemberFromRoom(selectedRoom.id, userId)
      await fetchRoomMembers(selectedRoom.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member')
    }
  }

  if (!isAdmin) {
    return null
  }

  const memberUserIds = new Set(roomMembers.map((m) => m.user_id))
  const availableUsers = users.filter((u) => !memberUserIds.has(u.id))

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Channel Management" size="large">
      <div className="flex flex-col md:flex-row gap-6 h-[60vh]">
        {/* Left Panel - Room List */}
        <div className="w-full md:w-1/2 flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium text-gray-900">Channels</h4>
            <button
              onClick={() => setShowCreateRoom(true)}
              className="px-3 py-1 text-sm bg-primary text-white rounded hover:bg-purple-800 transition-colors"
            >
              + New Channel
            </button>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 px-3 py-2 rounded text-sm mb-4">
              {error}
              <button onClick={() => setError(null)} className="ml-2 underline">
                Dismiss
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-gray-500 text-sm">Loading channels...</div>
          ) : (
            <div className="flex-1 overflow-y-auto border rounded-lg">
              {rooms.map((room) => (
                <div
                  key={room.id}
                  onClick={() => !room.is_default && setSelectedRoom(room)}
                  className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                    selectedRoom?.id === room.id ? 'bg-purple-50' : ''
                  } ${room.is_default ? 'cursor-default' : ''}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{room.name}</span>
                        {room.is_default && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                            Default
                          </span>
                        )}
                      </div>
                      {room.description && (
                        <p className="text-sm text-gray-500 mt-1">{room.description}</p>
                      )}
                    </div>
                    {!room.is_default && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteRoom(room)
                        }}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete channel"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Panel - Member Management */}
        <div className="w-full md:w-1/2 flex flex-col">
          {selectedRoom ? (
            <>
              <h4 className="font-medium text-gray-900 mb-4">
                Members of #{selectedRoom.name}
              </h4>

              {loadingMembers ? (
                <div className="text-gray-500 text-sm">Loading members...</div>
              ) : (
                <>
                  {/* Current Members */}
                  <div className="flex-1 overflow-y-auto border rounded-lg mb-4">
                    {roomMembers.length === 0 ? (
                      <div className="p-4 text-gray-500 text-sm text-center">
                        No members yet. Add users below.
                      </div>
                    ) : (
                      roomMembers.map((member) => (
                        <div
                          key={member.id}
                          className="p-3 border-b last:border-b-0 flex justify-between items-center"
                        >
                          <div>
                            <span className="font-medium">
                              {member.display_name || 'Unknown User'}
                            </span>
                            {member.account_type && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({member.account_type})
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveMember(member.user_id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Member Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Add User
                    </label>
                    <select
                      className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                      value=""
                      onChange={(e) => e.target.value && handleAddMember(e.target.value)}
                    >
                      <option value="">Select a user to add...</option>
                      {availableUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.display_name || 'Unknown'} ({user.account_type || 'user'})
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
              Select a channel to manage members
              <br />
              <span className="text-xs mt-1">(Default channel is open to all users)</span>
            </div>
          )}
        </div>
      </div>

      {/* Create Room Modal */}
      {showCreateRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-medium mb-4">Create New Channel</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel Name *
                </label>
                <input
                  type="text"
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Sales Team"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newRoomDescription}
                  onChange={(e) => setNewRoomDescription(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Optional description"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowCreateRoom(false)
                    setNewRoomName('')
                    setNewRoomDescription('')
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRoom}
                  disabled={!newRoomName.trim() || creating}
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-purple-800 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Channel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

